import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  LayoutDashboard,
  Calculator,
  Calendar,
  FileText,
  User,
  LogOut,
  Menu,
  X,
  Euro,
  Percent,
  ArrowRightLeft,
  Clock,
  Bell,
  Upload,
  Download,
  Trash2,
  Loader2,
  MessageSquare,
  BellRing,
  Save,
  Edit,
  Video,
  CreditCard,
  CheckCircle,
  XCircle,
  RefreshCw,
  ShieldAlert,
  Search,
  Building2,
  AlertTriangle,
  UserSearch
} from 'lucide-react';
import { CrifForm } from '@/components/CrifForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// Dashboard Home
const DashboardHome = ({ user }) => {
  const [consultations, setConsultations] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const { isSupported, isSubscribed, subscribe, unsubscribe } = usePushNotifications();
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [cRes, dRes, deadlinesRes] = await Promise.all([
          axios.get(`${API}/consultations`, { headers: authHeaders() }),
          axios.get(`${API}/documents`, { headers: authHeaders() }),
          axios.get(`${API}/utilities/upcoming-deadlines`)
        ]);
        setConsultations(cRes.data);
        setDocCount(dRes.data.length);
        setUpcomingDeadlines(deadlinesRes.data);
        
        // Browser push notification for critical deadlines
        if (deadlinesRes.data.some(d => d.urgency === 'critical') && 'Notification' in window) {
          if (Notification.permission === 'default') {
            Notification.requestPermission();
          }
          if (Notification.permission === 'granted') {
            const critical = deadlinesRes.data.filter(d => d.urgency === 'critical');
            critical.forEach(d => {
              new Notification('Scadenza fiscale imminente!', {
                body: `${d.titolo} - ${d.data_scadenza} (tra ${d.days_until} giorni)`,
                icon: '/favicon.ico'
              });
            });
          }
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetch();
  }, []);

  const statusLabel = (s) => {
    const map = { confirmed: 'Confermata', completed: 'Completata', cancelled: 'Annullata' };
    return map[s] || 'In attesa';
  };

  const statusColor = (s) => {
    const map = { confirmed: 'bg-green-100 text-green-700', completed: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Benvenuto, {user?.nome}!
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Gestisci le tue consulenze e accedi alle utility aziendali
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{consultations.length}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Consulenze</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="doc-count">{docCount}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Documenti</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">6</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Scadenze</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Deadlines Notification Banner */}
      {upcomingDeadlines.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-5" data-testid="deadline-notifications">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center animate-pulse">
              <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Scadenze in arrivo</h3>
          </div>
          <div className="space-y-2">
            {upcomingDeadlines.map((d) => (
              <div key={d.id} className={`flex items-center justify-between p-3 rounded-lg ${
                d.urgency === 'critical' ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800' :
                d.urgency === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-200' :
                'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
              }`} data-testid={`deadline-${d.id}`}>
                <div>
                  <p className="font-medium text-sm text-slate-900 dark:text-white">{d.titolo}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{d.categoria} - {d.data_scadenza}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                  d.urgency === 'critical' ? 'bg-red-600 text-white' :
                  d.urgency === 'warning' ? 'bg-amber-500 text-white' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {d.days_until === 0 ? 'OGGI' : d.days_until === 1 ? 'DOMANI' : `${d.days_until}g`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Push Notification Banner */}
      {isSupported && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between" data-testid="push-notification-banner">
          <div className="flex items-center gap-3">
            <BellRing className={`w-5 h-5 ${isSubscribed ? 'text-green-500' : 'text-slate-400'}`} />
            <div>
              <p className="font-medium text-sm text-slate-900 dark:text-white">
                {isSubscribed ? 'Notifiche push attive' : 'Attiva le notifiche push'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {isSubscribed ? 'Riceverai avvisi per le scadenze fiscali imminenti' : 'Ricevi avvisi automatici per le scadenze anche quando il sito è chiuso'}
              </p>
            </div>
          </div>
          <Button
            variant={isSubscribed ? 'outline' : 'default'}
            size="sm"
            onClick={async () => {
              setPushLoading(true);
              if (isSubscribed) {
                const ok = await unsubscribe();
                if (ok) toast.success('Notifiche disattivate');
              } else {
                const ok = await subscribe();
                if (ok) toast.success('Notifiche attivate!');
                else toast.error('Impossibile attivare le notifiche');
              }
              setPushLoading(false);
            }}
            disabled={pushLoading}
            data-testid="push-toggle-btn"
          >
            {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSubscribed ? 'Disattiva' : 'Attiva'}
          </Button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Consulenze recenti</h2>
        {consultations.length > 0 ? (
          <div className="space-y-4">
            {consultations.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{c.tipo_consulenza}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{c.data_preferita} alle {c.ora_preferita}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(c.stato)}`}>
                  {statusLabel(c.stato)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-500 dark:text-slate-400 text-center py-8">
            Nessuna consulenza. <Link to="/consulenza" className="text-indigo-600 hover:underline">Prenota ora</Link>
          </p>
        )}
      </div>
    </div>
  );
};

// VAT Calculator
const VATCalculator = () => {
  const [importo, setImporto] = useState('');
  const [aliquota, setAliquota] = useState('22');
  const [tipo, setTipo] = useState('scorporo');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    if (!importo) { toast.error('Inserisci un importo'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/utilities/vat-calculator`, {
        importo: parseFloat(importo), aliquota: parseFloat(aliquota), tipo
      });
      setResult(response.data);
    } catch { toast.error('Errore nel calcolo'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Calcolatore IVA</h1>
      <p className="text-slate-600 dark:text-slate-400 mb-8">Calcola scorporo o aggiunta IVA</p>
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-6">
        <div>
          <Label>Tipo di calcolo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="mt-2" data-testid="vat-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="scorporo"><div className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" />Scorporo IVA</div></SelectItem>
              <SelectItem value="aggiunta"><div className="flex items-center gap-2"><Percent className="w-4 h-4" />Aggiunta IVA</div></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Importo (€)</Label>
          <div className="relative mt-2">
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input type="number" value={importo} onChange={(e) => setImporto(e.target.value)} placeholder="1000.00" className="pl-10" data-testid="vat-importo" />
          </div>
        </div>
        <div>
          <Label>Aliquota IVA</Label>
          <Select value={aliquota} onValueChange={setAliquota}>
            <SelectTrigger className="mt-2" data-testid="vat-aliquota"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="22">22% (Ordinaria)</SelectItem>
              <SelectItem value="10">10% (Ridotta)</SelectItem>
              <SelectItem value="5">5% (Super ridotta)</SelectItem>
              <SelectItem value="4">4% (Minima)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={calculate} className="w-full btn-accent" disabled={loading} data-testid="vat-calculate">
          <Calculator className="w-5 h-5 mr-2" />Calcola
        </Button>
        {result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-6 space-y-4">
            <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">Imponibile</span><span className="text-xl font-semibold text-slate-900 dark:text-white">€{result.imponibile.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">IVA ({aliquota}%)</span><span className="text-xl font-semibold text-indigo-600">€{result.iva.toFixed(2)}</span></div>
            <div className="border-t border-slate-200 dark:border-slate-600 pt-4 flex justify-between"><span className="font-medium text-slate-900 dark:text-white">Totale</span><span className="text-2xl font-bold text-slate-900 dark:text-white">€{result.totale.toFixed(2)}</span></div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Fiscal Deadlines with Export
const FiscalDeadlines = () => {
  const [deadlines, setDeadlines] = useState([]);

  useEffect(() => {
    axios.get(`${API}/utilities/fiscal-deadlines`).then(r => setDeadlines(r.data)).catch(console.error);
  }, []);

  const getCategoryColor = (cat) => {
    const c = { 'IVA': 'bg-blue-100 text-blue-700', 'Dichiarazioni': 'bg-green-100 text-green-700', 'Contributi': 'bg-purple-100 text-purple-700', 'Imposte': 'bg-amber-100 text-amber-700' };
    return c[cat] || 'bg-slate-100 text-slate-700';
  };

  const exportCalendar = async () => {
    try {
      const response = await axios.get(`${API}/utilities/export-calendar`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'scadenze-fiscali.ics';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Calendario esportato!');
    } catch { toast.error('Errore nell\'esportazione'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Scadenze Fiscali</h1>
          <p className="text-slate-600 dark:text-slate-400">Tieni traccia delle scadenze fiscali</p>
        </div>
        <Button variant="outline" onClick={exportCalendar} data-testid="export-calendar-btn">
          <Download className="w-4 h-4 mr-2" />
          Esporta .ics
        </Button>
      </div>
      <div className="space-y-4">
        {deadlines.map((d) => (
          <motion.div key={d.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 hover:border-indigo-200 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{d.titolo}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">{d.descrizione}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(d.categoria)}`}>{d.categoria}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{new Date(d.data_scadenza).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</p>
                <p className="text-sm text-slate-500">{new Date(d.data_scadenza).getFullYear()}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Documents Upload
const Documents = () => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const r = await axios.get(`${API}/documents`, { headers: authHeaders() });
      setDocs(r.data);
    } catch { console.error('Error loading docs'); }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Seleziona un file'); return; }
    if (!docName.trim()) { toast.error('Inserisci un nome per il documento'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('nome', docName);
    formData.append('tipo', 'documento');

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato!');
      setDocName('');
      fileRef.current.value = '';
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`, { headers: authHeaders() });
      toast.success('Documento eliminato');
      fetchDocs();
    } catch { toast.error('Errore nell\'eliminazione'); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div data-testid="documents-section">
      <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Documenti</h1>
      <p className="text-slate-600 dark:text-slate-400 mb-8">Carica e gestisci i tuoi documenti</p>

      {/* Upload Form */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Carica nuovo documento</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Nome documento</Label>
            <Input value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Es: Fattura Q1 2026" className="mt-2" data-testid="doc-name-input" />
          </div>
          <div>
            <Label>File (max 10MB)</Label>
            <Input type="file" ref={fileRef} className="mt-2" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="doc-file-input" />
          </div>
          <Button onClick={handleUpload} disabled={uploading} className="btn-accent" data-testid="doc-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Caricamento...' : 'Carica'}
          </Button>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {docs.length > 0 ? (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-testid={`doc-row-${doc.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{doc.nome}</p>
                    <p className="text-xs text-slate-500">{formatSize(doc.size)} &middot; {new Date(doc.created_at).toLocaleDateString('it-IT')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer">
                    <Button variant="ghost" size="sm" data-testid={`doc-download-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </a>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="text-red-500 hover:text-red-700" data-testid={`doc-delete-${doc.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessun documento caricato</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Chat History
const ChatHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/chat/user-sessions`).then(r => { setSessions(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadSession = async (sessionId) => {
    setSelectedSession(sessionId);
    try {
      const r = await axios.get(`${API}/chat/history?session_id=${sessionId}`);
      setMessages(r.data);
    } catch { toast.error('Errore nel caricamento'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div data-testid="chat-history-section">
      <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Cronologia Chat</h1>
      <p className="text-slate-600 dark:text-slate-400 mb-8">Le tue conversazioni con l'assistente fiscale</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions List */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-white">Sessioni ({sessions.length})</h3>
          </div>
          {sessions.length > 0 ? (
            <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-[500px] overflow-y-auto">
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                  className={`w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${selectedSession === s.session_id ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-indigo-600' : ''}`}
                  data-testid={`chat-session-${s.session_id}`}
                >
                  <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{s.preview}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-400">{s.message_count} msg</span>
                    <span className="text-xs text-slate-400">&middot;</span>
                    <span className="text-xs text-slate-400">{new Date(s.last_active).toLocaleDateString('it-IT')}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nessuna conversazione</p>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {selectedSession ? (
            <>
              <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-900 dark:text-white">Conversazione</h3>
              </div>
              <div className="p-4 max-h-[500px] overflow-y-auto space-y-4">
                {messages.map((m, i) => (
                  <div key={i} className="space-y-3">
                    <div className="flex justify-end">
                      <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%]">
                        <p className="text-sm">{m.user_message}</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-slate-100 dark:bg-slate-700 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]">
                        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{m.bot_response}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Seleziona una sessione per vedere i messaggi</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Profile - Editable
const Profile = ({ user }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: user?.nome || '', cognome: user?.cognome || '',
    telefono: user?.telefono || '', azienda: user?.azienda || ''
  });

  useEffect(() => {
    if (user) setForm({ nome: user.nome || '', cognome: user.cognome || '', telefono: user.telefono || '', azienda: user.azienda || '' });
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, form, { headers: authHeaders() });
      toast.success('Profilo aggiornato!');
      setEditing(false);
    } catch { toast.error('Errore nell\'aggiornamento'); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="profile-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Il mio profilo</h1>
          <p className="text-slate-600 dark:text-slate-400">Gestisci le tue informazioni</p>
        </div>
        {!editing && (
          <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
            <Edit className="w-4 h-4 mr-2" />Modifica
          </Button>
        )}
      </div>
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center">
            <User className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{user?.nome} {user?.cognome}</h2>
            <p className="text-slate-500 dark:text-slate-400">{user?.email}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-200 dark:border-slate-700">
          <div>
            <Label className="text-slate-500 dark:text-slate-400">Nome</Label>
            {editing ? <Input value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} className="mt-1" data-testid="profile-nome" />
              : <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.nome}</p>}
          </div>
          <div>
            <Label className="text-slate-500 dark:text-slate-400">Cognome</Label>
            {editing ? <Input value={form.cognome} onChange={(e) => setForm({...form, cognome: e.target.value})} className="mt-1" data-testid="profile-cognome" />
              : <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.cognome}</p>}
          </div>
          <div>
            <Label className="text-slate-500 dark:text-slate-400">Email</Label>
            <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.email}</p>
          </div>
          <div>
            <Label className="text-slate-500 dark:text-slate-400">Telefono</Label>
            {editing ? <Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} className="mt-1" data-testid="profile-telefono" />
              : <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.telefono || '-'}</p>}
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-500 dark:text-slate-400">Azienda</Label>
            {editing ? <Input value={form.azienda} onChange={(e) => setForm({...form, azienda: e.target.value})} className="mt-1" data-testid="profile-azienda" />
              : <p className="font-medium text-slate-900 dark:text-white mt-1">{user?.azienda || '-'}</p>}
          </div>
        </div>
        {editing && (
          <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button onClick={handleSave} disabled={saving} className="btn-accent" data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)}>Annulla</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Consultations Real-time Status
const MyConsultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConsultations = async () => {
    try {
      const r = await axios.get(`${API}/consultations`, { headers: authHeaders() });
      setConsultations(r.data);
    } catch { console.error('Error loading consultations'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchConsultations();
    const interval = setInterval(fetchConsultations, 30000);
    return () => clearInterval(interval);
  }, []);

  const statusConfig = {
    pending: { label: 'In attesa', cls: 'bg-amber-100 text-amber-700', icon: Clock },
    confirmed: { label: 'Confermata', cls: 'bg-green-100 text-green-700', icon: CheckCircle },
    completed: { label: 'Completata', cls: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    cancelled: { label: 'Annullata', cls: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const paymentConfig = {
    paid: { label: 'Pagato', cls: 'bg-green-100 text-green-700' },
    pending: { label: 'In attesa', cls: 'bg-amber-100 text-amber-700' },
  };

  const tipoLabels = {
    'inizio-attivita': 'Inizio Attività', 'fiscale-generale': 'Fiscale Generale',
    'internazionale': 'Internazionale', 'ecommerce': 'E-commerce', 'contabile': 'Contabile'
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div data-testid="my-consultations-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Le mie consulenze</h1>
          <p className="text-slate-600 dark:text-slate-400">Stato aggiornato in tempo reale</p>
        </div>
        <Button variant="outline" onClick={fetchConsultations} data-testid="refresh-consultations">
          <RefreshCw className="w-4 h-4 mr-2" />Aggiorna
        </Button>
      </div>

      {consultations.length > 0 ? (
        <div className="space-y-4">
          {consultations.map((c) => {
            const status = statusConfig[c.stato] || statusConfig.pending;
            const payment = paymentConfig[c.payment_status];
            const StatusIcon = status.icon;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-md transition-shadow"
                data-testid={`consultation-${c.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {tipoLabels[c.tipo_consulenza] || c.tipo_consulenza}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.cls}`}>
                        <StatusIcon className="w-3 h-3" />{status.label}
                      </span>
                      {payment && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${payment.cls}`}>
                          <CreditCard className="w-3 h-3" />{payment.label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{c.data_preferita}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{c.ora_preferita}</span>
                    </div>
                    {c.messaggio && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 line-clamp-2">{c.messaggio}</p>}
                  </div>
                  {c.stato === 'confirmed' && (
                    <a href="https://telcavoip-fiscaldatanext.my3cx.it/meet/aurelioazzano" target="_blank" rel="noreferrer">
                      <Button size="sm" className="btn-accent" data-testid={`join-meeting-${c.id}`}>
                        <Video className="w-4 h-4 mr-1" />Videoconferenza
                      </Button>
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 dark:text-slate-400 mb-4">Nessuna consulenza prenotata</p>
          <Link to="/consulenza"><Button className="btn-accent">Prenota ora</Button></Link>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Page
// ============== CLIENT RISK INTELLIGENCE ==============
const CLIENT_REPORTS = [
  { value: 'verifica_cf', label: 'Verifica Codice Fiscale', icon: CheckCircle, cat: 'persona', needsName: false, isCrif: false },
  { value: 'report_persona', label: 'Report Persona', icon: UserSearch, cat: 'persona', needsName: true, isCrif: false },
  { value: 'patrimoniale_persona', label: 'Report Patrimoniale', icon: Building2, cat: 'persona', needsName: true, isCrif: false },
  { value: 'negativita', label: 'Negatività e Protesti', icon: AlertTriangle, cat: 'persona', needsName: false, isCrif: false },
  { value: 'crif_persona', label: 'CRIF Persona', icon: ShieldAlert, cat: 'persona', needsName: false, isCrif: true },
  { value: 'eredi', label: 'Ricerca Eredi', icon: UserSearch, cat: 'persona', needsName: true, isCrif: false },
  { value: 'report_azienda', label: 'Report Azienda', icon: Building2, cat: 'azienda', needsName: false, isCrif: false },
  { value: 'crif_azienda', label: 'CRIF Azienda', icon: ShieldAlert, cat: 'azienda', needsName: false, isCrif: true },
];

const ClientRiskIntelligence = () => {
  const [selectedReport, setSelectedReport] = useState('verifica_cf');
  const [form, setForm] = useState({ codice_fiscale: '', partita_iva: '', nome: '', cognome: '', nome_azienda: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const cur = CLIENT_REPORTS.find(r => r.value === selectedReport);
  const isCompany = cur?.cat === 'azienda';

  const executeQuery = async () => {
    if (!form.codice_fiscale && !form.partita_iva) { toast.error('Inserisci un Codice Fiscale o una Partita IVA'); return; }
    if (cur?.needsName && (!form.nome || !form.cognome)) { toast.error('Nome e Cognome obbligatori'); return; }
    if (isCompany && !cur?.isCrif && !form.nome_azienda) { toast.error('Nome Azienda obbligatorio'); return; }
    setLoading(true); setResult(null);
    try {
      const res = await axios.post(`${API}/risk/query`, { report_type: selectedReport, ...form },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
      setResult(res.data);
      if (res.data.success) toast.success('Report completato');
      else toast.error(res.data.error || 'Errore');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    setLoading(false);
  };

  const executeCrifQuery = async (crifData) => {
    setLoading(true); setResult(null);
    try {
      const res = await axios.post(`${API}/risk/query`,
        { report_type: selectedReport, crif_data: crifData },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
      setResult(res.data);
      if (res.data.success) toast.success('Richiesta CRIF inviata');
      else toast.error(res.data.error || 'Errore CRIF');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore CRIF'); }
    setLoading(false);
  };

  const checkReportStatus = async (reportType, requestId) => {
    setCheckingStatus(true);
    try {
      const res = await axios.get(`${API}/risk/check-status/${reportType}/${requestId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});
      setResult(prev => ({ ...prev, data: res.data.data, state: res.data.state, success: res.data.success, has_download: res.data.has_download, error: res.data.error }));
      if (res.data.state === 'COMPLETE') toast.success('Report completato!');
      else if (res.data.state === 'PENDING') toast.info('Ancora in elaborazione. Riprova tra qualche minuto.');
      else toast.info(`Stato: ${res.data.state}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    setCheckingStatus(false);
  };

  const downloadReport = async (reportType, requestId) => {
    try {
      const res = await fetch(`${API}/risk/download/${reportType}/${requestId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) { toast.error('Download non disponibile'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${requestId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Errore download'); }
  };

  const renderData = (data, depth = 0) => {
    if (!data || typeof data !== 'object') return <span className="text-slate-700 dark:text-slate-300">{String(data)}</span>;
    if (Array.isArray(data)) return <div className="space-y-1 ml-4">{data.map((item, i) => <div key={i} className="border-l-2 border-slate-200 pl-3">{renderData(item, depth+1)}</div>)}</div>;
    return (
      <div className={`space-y-1 ${depth > 0 ? 'ml-4' : ''}`}>
        {Object.entries(data).map(([key, val]) => (
          <div key={key} className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase min-w-[140px]">{key.replace(/_/g, ' ')}:</span>
            {typeof val === 'object' && val !== null ? renderData(val, depth+1) : (
              <span className="text-sm text-slate-800 dark:text-slate-200">{val === true ? 'Sì' : val === false ? 'No' : val === null ? '-' : String(val)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="client-risk-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">TelcaVoIP Risk Intelligence</h1>
          <p className="text-sm text-slate-500">Analisi rischio e visure</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {CLIENT_REPORTS.map(opt => {
          const Icon = opt.icon;
          return (
            <button key={opt.value} onClick={() => { setSelectedReport(opt.value); setResult(null); }}
              className={`p-3 rounded-xl border-2 text-center transition-all ${selectedReport === opt.value ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
              data-testid={`client-risk-type-${opt.value}`}>
              <Icon className="w-5 h-5 mx-auto mb-1 text-slate-600 dark:text-slate-400" />
              <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{opt.label}</p>
            </button>
          );
        })}
      </div>

      {cur?.isCrif ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">{cur.label} - Form Avanzato</h2>
          <CrifForm type={selectedReport === 'crif_azienda' ? 'azienda' : 'persona'} onSubmit={executeCrifQuery} loading={loading} />
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Codice Fiscale *</Label>
              <Input value={form.codice_fiscale} onChange={e => setForm(f => ({...f, codice_fiscale: e.target.value.toUpperCase()}))} placeholder="RSSMRA80A01H501U" className="mt-1 uppercase" data-testid="client-risk-cf" />
            </div>
            {isCompany && (
              <>
                <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={e => setForm(f => ({...f, partita_iva: e.target.value}))} placeholder="12345678901" className="mt-1" data-testid="client-risk-piva" /></div>
                <div><Label>Nome Azienda *</Label><Input value={form.nome_azienda} onChange={e => setForm(f => ({...f, nome_azienda: e.target.value}))} placeholder="OpenAPI Srl" className="mt-1" data-testid="client-risk-company" /></div>
              </>
            )}
            {!isCompany && cur?.needsName && (
              <>
                <div><Label>Nome *</Label><Input value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} placeholder="Mario" className="mt-1" data-testid="client-risk-nome" /></div>
                <div><Label>Cognome *</Label><Input value={form.cognome} onChange={e => setForm(f => ({...f, cognome: e.target.value}))} placeholder="Rossi" className="mt-1" data-testid="client-risk-cognome" /></div>
              </>
            )}
          </div>
          <Button onClick={executeQuery} disabled={loading || (!form.codice_fiscale && !form.partita_iva)} className="mt-4 bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white" data-testid="client-risk-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Esegui {cur?.label}
          </Button>
        </div>
      )}

      {result && (
        <div className={`bg-white dark:bg-slate-800 rounded-xl border-2 p-5 ${result.success ? 'border-emerald-200' : 'border-red-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium ${result.success ? 'text-emerald-600' : 'text-red-600'}`}>{result.report_label} — {result.success ? (result.state === 'PENDING' ? 'In elaborazione' : 'Completato') : 'Errore'}</p>
              {result.state && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${result.state === 'PENDING' ? 'bg-amber-100 text-amber-700 animate-pulse' : result.state === 'COMPLETE' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  {result.state}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {result.state === 'PENDING' && result.api_request_id && (
                <Button size="sm" variant="outline" onClick={() => checkReportStatus(result.report_type, result.api_request_id)} disabled={checkingStatus} data-testid="client-risk-check-status">
                  {checkingStatus ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Controlla
                </Button>
              )}
              {result.state === 'COMPLETE' && result.has_download && result.api_request_id && (
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => downloadReport(result.report_type, result.api_request_id)} data-testid="client-risk-download">
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
              )}
            </div>
          </div>
          {result.state === 'PENDING' && (
            <p className="text-xs text-amber-600 mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded">Report in elaborazione. Clicca "Controlla" per verificare lo stato.</p>
          )}
          {result.error && <p className="text-sm text-red-600 mb-3">{typeof result.error === 'string' ? result.error : JSON.stringify(result.error)}</p>}
          {result.data && <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 overflow-auto max-h-[400px]">{renderData(result.data)}</div>}
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasRiskAccess, setHasRiskAccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/risk/my-access`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }})
        .then(r => setHasRiskAccess(r.data.risk_access))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const navItems = [
    { path: '/area-clienti', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/area-clienti/consulenze', label: 'Consulenze', icon: Calendar },
    { path: '/area-clienti/documenti', label: 'Documenti', icon: FileText },
    { path: '/area-clienti/calcolatore-iva', label: 'Calcolatore IVA', icon: Calculator },
    { path: '/area-clienti/scadenze', label: 'Scadenze', icon: Clock },
    { path: '/area-clienti/chat-history', label: 'Cronologia Chat', icon: MessageSquare },
    ...(hasRiskAccess ? [{ path: '/area-clienti/risk', label: 'Risk Intelligence', icon: ShieldAlert }] : []),
    { path: '/area-clienti/profilo', label: 'Profilo', icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="dashboard-page">
      <div className="lg:hidden bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2"><Menu className="w-6 h-6 dark:text-white" /></button>
        <span className="font-semibold dark:text-white">Area Clienti</span>
        <div className="w-10" />
      </div>

      <div className="flex">
        <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><span className="text-white font-bold text-sm">SA</span></div>
              <span className="font-semibold text-slate-900 dark:text-white">Area Clienti</span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden"><X className="w-5 h-5 dark:text-white" /></button>
          </div>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                <item.icon className="w-5 h-5" />{item.label}
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 dark:border-slate-700">
            <button onClick={logout} className="sidebar-item w-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700" data-testid="dashboard-logout">
              <LogOut className="w-5 h-5" />Esci
            </button>
          </div>
        </aside>

        {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        <main className="flex-1 p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardHome user={user} />} />
            <Route path="consulenze" element={<MyConsultations />} />
            <Route path="documenti" element={<Documents />} />
            <Route path="calcolatore-iva" element={<VATCalculator />} />
            <Route path="scadenze" element={<FiscalDeadlines />} />
            <Route path="chat-history" element={<ChatHistory />} />
            <Route path="risk" element={<ClientRiskIntelligence />} />
            <Route path="profilo" element={<Profile user={user} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
